// Supported locales. English is the source language and lives at the site root (no /en prefix).
export type LocaleCode = 'en' | 'zh-Hans' | 'ms' | 'ta'

export interface LocaleInfo {
  code: LocaleCode
  /** URL prefix, '' for English. */
  prefix: '' | '/zh' | '/ms' | '/ta'
  /** <html lang> and hreflang value. */
  htmlLang: string
  hreflang: string
  /** Intl locale for dates. Money always uses en-SG grouping so S$ amounts read the same in every language. */
  intl: string
  /** Name of the language in itself, for the switcher. */
  label: string
  /** Statutory dates are written out with a month name, never numeric-only. */
  monthStyle: 'short' | 'long'
}

export const LOCALES: LocaleInfo[] = [
  { code: 'en', prefix: '', htmlLang: 'en-SG', hreflang: 'en', intl: 'en-GB', label: 'English', monthStyle: 'short' },
  { code: 'zh-Hans', prefix: '/zh', htmlLang: 'zh-Hans', hreflang: 'zh-Hans', intl: 'zh-Hans-SG', label: '简体中文', monthStyle: 'long' },
  { code: 'ms', prefix: '/ms', htmlLang: 'ms', hreflang: 'ms', intl: 'ms-SG', label: 'Bahasa Melayu', monthStyle: 'long' },
  { code: 'ta', prefix: '/ta', htmlLang: 'ta', hreflang: 'ta', intl: 'ta-SG', label: 'தமிழ்', monthStyle: 'long' },
]
export const TRANSLATED = LOCALES.filter(l => l.code !== 'en')
export const localeInfo = (code: LocaleCode) => LOCALES.find(l => l.code === code)!

/** Splits "/zh/when" into { locale: 'zh-Hans', path: '/when' }. Unprefixed paths are English. */
export function splitLocale(pathname: string): { locale: LocaleCode; path: string } {
  for (const l of TRANSLATED) {
    if (pathname === l.prefix || pathname.startsWith(l.prefix + '/')) return { locale: l.code, path: pathname.slice(l.prefix.length) || '/' }
  }
  return { locale: 'en', path: pathname || '/' }
}

/** "/when" in zh-Hans → "/zh/when"; "/" → "/zh/". Leaves external URLs and bare #anchors alone. */
export function localizePath(href: string, locale: LocaleCode): string {
  if (!href.startsWith('/') || locale === 'en') return href
  const { prefix } = localeInfo(locale)
  if (href === '/' || href.startsWith('/#')) return prefix + href
  return prefix + href
}

/** ISO date → statutory date written out in the locale, e.g. "1 Apr 2028", "2028年4月1日", "1 April 2028". */
export function fmtStatutory(iso: string, locale: LocaleCode): string {
  const l = localeInfo(locale)
  return new Date(iso + 'T00:00:00Z').toLocaleDateString(l.intl, { day: 'numeric', month: l.monthStyle, year: 'numeric', timeZone: 'UTC' })
}

/** S$ amounts use the same digits and grouping in every locale (S$200,000), so they are never ambiguous. */
export const money = (n: number) => 'S$' + n.toLocaleString('en-SG')
